import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { where } from 'firebase/firestore'
import { useAuth } from '../../lib/auth'
import { useCollection, useDocument } from '../../lib/firestore'
import { useTourAccess } from '../../lib/tours'
import { countdown, formatDayLong, formatTime } from '../../lib/format'
import type { Show, Tour, WithId } from '../../types/models'
import Icon from '../../components/Icon'
import MobileHeader, { MobileScreen, SectionLabel } from '../../components/mobile/MobileHeader'
import Empty from '../../components/Empty'
import ShowDialog from '../../dialogs/ShowDialog'

/**
 * Shows.
 *
 * The next show gets the whole card, with the three times a crew actually
 * needs before they arrive. Everything after it is a scannable row — date
 * block, city, venue, set time — because past the next one you're looking for
 * a specific date, not reading.
 */
export default function MobileShows() {
  const { tourId } = useParams<{ tourId: string }>()
  const { uid } = useAuth()
  const { data: tour } = useDocument<Tour>('tours', tourId)
  const access = useTourAccess(tour, uid)
  const canEdit = access?.canEdit ?? false

  const shows = useCollection<Show>('shows', tourId ? [where('tourId', '==', tourId)] : null)
  const [adding, setAdding] = useState(false)

  const sorted = [...shows.data].sort((a, b) => a.date.toMillis() - b.date.toMillis())
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const nextShow = sorted.find((show) => show.date.toDate() >= today)
  const upcoming = sorted.filter(
    (show) => show.date.toDate() >= today && show.id !== nextShow?.id,
  )
  const past = sorted.filter((show) => show.date.toDate() < today).reverse()

  return (
    <>
      <MobileHeader
        title="Shows"
        back={`/tours/${tourId}`}
        action={
          canEdit
            ? { icon: 'plus', label: 'Add show', accent: true, onClick: () => setAdding(true) }
            : undefined
        }
      />
      <MobileScreen>
        {!shows.loading && shows.data.length === 0 && (
          <Empty
            title="No shows yet"
            message="Add the first date on this tour."
            action={canEdit ? { label: 'Add a show', onClick: () => setAdding(true) } : undefined}
          />
        )}

        {nextShow && (
          <Link
            to={`/tours/${tourId}/shows/${nextShow.id}`}
            className="panel panel-signalled"
            style={{ display: 'block', padding: 16 }}
          >
            <span className="row" style={{ gap: 7, marginBottom: 8 }}>
              <span className="dot dot-accent" />
              <span className="t-eyebrow accent">Next show</span>
            </span>

            <div className="spread" style={{ gap: 12, alignItems: 'flex-start' }}>
              <div className="stack" style={{ gap: 2, minWidth: 0 }}>
                <span className="t-display-m" style={{ lineHeight: 1.05 }}>
                  {nextShow.city}
                </span>
                <span className="t-caption truncate">{nextShow.venueName}</span>
                <span className="t-spec-caption faint">
                  {formatDayLong(nextShow.date, nextShow.timezone)}
                </span>
              </div>
              <span className="chip" style={{ flex: 'none' }}>
                {countdown(nextShow.date)}
              </span>
            </div>

            {(nextShow.loadIn || nextShow.doorsOpen || nextShow.headlinerSetTime) && (
              <div
                className="row"
                style={{
                  gap: 22,
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: '1px solid var(--hairline)',
                }}
              >
                <Timing label="Load in" value={nextShow.loadIn} zone={nextShow.timezone} />
                <Timing label="Doors" value={nextShow.doorsOpen} zone={nextShow.timezone} />
                <Timing
                  label="Set"
                  value={nextShow.headlinerSetTime}
                  zone={nextShow.timezone}
                  accent
                />
              </div>
            )}
          </Link>
        )}

        {upcoming.length > 0 && (
          <section className="stack" style={{ gap: 8 }}>
            <SectionLabel
              trailing={<span className="t-spec-caption faint">{upcoming.length}</span>}
            >
              Upcoming
            </SectionLabel>
            <div className="stack" style={{ gap: 10 }}>
              {upcoming.map((show) => (
                <ShowRow key={show.id} show={show} tourId={tourId!} />
              ))}
            </div>
          </section>
        )}

        {past.length > 0 && (
          <section className="stack" style={{ gap: 8 }}>
            <SectionLabel>Played</SectionLabel>
            <div className="stack" style={{ gap: 10 }}>
              {past.map((show) => (
                <ShowRow key={show.id} show={show} tourId={tourId!} muted />
              ))}
            </div>
          </section>
        )}
      </MobileScreen>

      {adding && uid && tourId && (
        <ShowDialog
          open
          tourId={tourId}
          userId={uid}
          onClose={() => setAdding(false)}
        />
      )}
    </>
  )
}

function ShowRow({
  show,
  tourId,
  muted,
}: {
  show: WithId<Show>
  tourId: string
  muted?: boolean
}) {
  const date = show.date.toDate()
  const month = new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    ...(show.timezone ? { timeZone: show.timezone } : {}),
  })
    .format(date)
    .toUpperCase()
  const day = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    ...(show.timezone ? { timeZone: show.timezone } : {}),
  }).format(date)

  return (
    <Link
      to={`/tours/${tourId}/shows/${show.id}`}
      className="panel row"
      style={{ padding: '14px 16px', gap: 14, opacity: muted ? 0.65 : 1 }}
    >
      <div className="stack" style={{ gap: 0, alignItems: 'center', width: 34, flex: 'none' }}>
        <span className="t-eyebrow" style={{ fontSize: '0.55rem' }}>
          {month}
        </span>
        <span className="t-spec" style={{ fontSize: '1.2rem', fontWeight: 600 }}>
          {day}
        </span>
      </div>

      <span
        aria-hidden
        style={{ width: 1, alignSelf: 'stretch', background: 'var(--hairline)' }}
      />

      <div className="grow stack" style={{ gap: 1, minWidth: 0 }}>
        <span className="truncate" style={{ fontWeight: 600 }}>
          {show.city}
        </span>
        <span className="t-caption truncate">{show.venueName}</span>
      </div>

      {show.headlinerSetTime && (
        <div className="stack" style={{ gap: 0, alignItems: 'flex-end', flex: 'none' }}>
          <span className="t-eyebrow" style={{ fontSize: '0.55rem' }}>
            Set
          </span>
          <span className="t-timecode">
            {formatTime(show.headlinerSetTime, show.timezone)}
          </span>
        </div>
      )}

      <Icon name="chevronRight" size={15} style={{ color: 'var(--text-tertiary)' }} />
    </Link>
  )
}

function Timing({
  label,
  value,
  zone,
  accent,
}: {
  label: string
  value?: { toDate: () => Date } | null
  zone?: string | null
  accent?: boolean
}) {
  return (
    <div className="stack" style={{ gap: 1 }}>
      <span className="t-eyebrow" style={{ fontSize: '0.55rem' }}>
        {label}
      </span>
      <span
        className="t-timecode"
        style={{ color: accent ? 'var(--accent-text)' : undefined }}
      >
        {value ? formatTime(value as never, zone) : '—'}
      </span>
    </div>
  )
}
